import { Drawer, type DrawerRootProps } from "@base-ui/react/drawer";
import { ArrowLeftIcon, CloseIcon } from "@obs/icons";
import { breakpoints, colors, measure, radius, zIndices } from "@obs/tokens/index.stylex.ts";
import type { StyleXStyles } from "@stylexjs/stylex";
import * as stylex from "@stylexjs/stylex";
import type { Dispatch, ReactNode, SetStateAction } from "react";

import { useThemeContainer } from "../../../theme/ThemeContainerContext";
import { Sticky } from "../../Sticky";
import { TopBar } from "../../TopBar";

export type DefaultProps = Omit<DrawerRootProps, "onOpenChange"> & {
	sx?: StyleXStyles;
	children: ReactNode;
	onBack?: () => void;
	onOpenChange?: Dispatch<SetStateAction<boolean>>;
};

const slideIn = stylex.keyframes({
	from: { transform: "translateY(100dvh)" },
	to: { transform: "translateY(0)" },
});

const fadeIn = stylex.keyframes({
	from: { opacity: 0 },
	to: { opacity: 1 },
});

const fadeOut = stylex.keyframes({
	from: { opacity: 1 },
	to: { opacity: 0 },
});

const styles = stylex.create({
	backdrop: {
		backdropFilter: "blur(2px)",
		backgroundColor: "rgba(0, 0, 0, 0.5)",
		inset: 0,
		position: "fixed",
		zIndex: zIndices.modal,
		animationDuration: "300ms",
		animationTimingFunction: "ease-in-out",
		animationFillMode: "forwards",
	},
	backdropOpen: {
		animationName: fadeIn,
	},
	backdropClosed: {
		animationName: fadeOut,
	},
	closed: {
		transform: "translateY(100dvh)",
	},
	content: {
		display: "flex",
		flexDirection: "column",
		flex: 1,
	},
	open: {
		animationDuration: "300ms",
		animationFillMode: "backwards",
		animationName: slideIn,
		animationTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
		transform: "translateY(0)",
	},
	popup: {
		backgroundColor: colors.neutralBgDefault,
		overscrollBehavior: "none",
		blockSize: {
			default: "100%",
			[breakpoints.md]: "auto",
		},
		borderRadius: {
			default: 0,
			[breakpoints.md]: radius.moderate,
		},
		display: "flex",
		flexDirection: "column",
		inlineSize: "100%",
		maxBlockSize: "100%",
		overflowY: "auto",
		paddingBlockEnd: "env(safe-area-inset-bottom)",
		maxInlineSize: {
			default: "100%",
			[breakpoints.md]: "662px",
		},
		transition: "transform 250ms cubic-bezier(0.4, 0, 0.2, 1)",
	},
	viewport: {
		alignItems: "center",
		display: "flex",
		inset: 0,
		justifyContent: "center",
		padding: {
			default: 0,
			[breakpoints.md]: measure.sizeL,
		},
		position: "fixed",
		zIndex: zIndices.modal,
	},
});

export const ModalPrimitive = ({ children, open, onOpenChange, sx, onBack }: DefaultProps) => {
	const themeContainer = useThemeContainer();
	return (
		<Drawer.Root open={open} onOpenChange={onOpenChange} swipeDirection="down">
			<Drawer.Portal container={themeContainer}>
				<Drawer.Backdrop {...stylex.props(styles.backdrop, open ? styles.backdropOpen : styles.backdropClosed)} />
				<Drawer.Viewport {...stylex.props(styles.viewport)}>
					<Drawer.Popup {...stylex.props(styles.popup, open ? styles.open : styles.closed, sx)}>
						<Sticky position="top">
							<TopBar.Default
								slotStart={onBack && <TopBar.ActionIcon icon={{ Component: ArrowLeftIcon }} onClick={onBack} />}
								slotEnd={<Drawer.Close render={<TopBar.ActionIcon icon={{ Component: CloseIcon }} />} />}
							/>
						</Sticky>

						<Drawer.Content {...stylex.props(styles.content)}>{children}</Drawer.Content>
					</Drawer.Popup>
				</Drawer.Viewport>
			</Drawer.Portal>
		</Drawer.Root>
	);
};
